import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ActivityIcon,
  BadgeCheckIcon,
  BellIcon,
  BrainCircuitIcon,
  BuildingIcon,
  BoxesIcon,
  CircleDotIcon,
  DatabaseIcon,
  FileBarChart2Icon,
  FilmIcon,
  KeyRoundIcon,
  KeySquareIcon,
  MapIcon,
  MonitorIcon,
  ServerIcon,
  LayoutGridIcon,
  Network as NetworkIcon,
  ScrollTextIcon,
  Settings as SettingsIcon,
  Upload as UploadIcon,
  HeartPulseIcon,
  LayoutDashboardIcon,
  LockIcon,
  LogOutIcon,
  PlayCircleIcon,
  ShieldCheckIcon,
  SlidersHorizontalIcon,
  UsersIcon,
  VideoIcon,
} from "lucide-react";

import { useAlertEventsSummary } from "@/api/alerts";
import { useLicense } from "@/api/license";
import { Button } from "@/components/ui/button";
import { logout as logoutApi } from "@/api/auth";
import { endSession } from "@/store/session";
import { NotificationBell } from "@/components/NotificationBell";
import { TenantSwitcher } from "@/components/TenantSwitcher";
import { TrialBanner } from "@/components/TrialBanner";
import { useAuthStore } from "@/store/auth";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useStatusFeed } from "@/api/status_feed";
import { useTenantStore } from "@/store/tenant";
import { navItemVisible } from "./navVisibility";
import { cn } from "@/lib/utils";

const navBase =
  "flex items-center gap-2 rounded px-3 py-2 text-sm transition-colors";

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  auditorOk?: boolean;
  superAdminOnly?: boolean;
  // P5 — permission-derived gating. When set, the item is hidden unless the
  // user holds this S4 permission key (from /auth/me). Additive: the legacy
  // adminOnly/superAdminOnly/auditorOk flags still apply for un-migrated
  // roles. Operator is the reference pattern; other roles migrate later.
  requiredPermission?: string;
  // S18B-2 hardening — when set, the item is ALSO hidden unless the tenant's
  // license carries this FEATURE flag (from useLicense().features — the SAME
  // source the target page self-gates on). Checked in Layout, not navItemVisible
  // (which has no access to license features), so a nav link never dead-ends on a
  // "not available on your plan" card.
  requiredFeature?: string;
}

// U1b-FE — the visibility predicate lives in ./navVisibility (unit-tested).

const NAV: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboardIcon, requiredPermission: "live_view" },
  { to: "/sites", label: "Sites", icon: BuildingIcon, requiredPermission: "live_view" },
  // S18B-1 — asset register (admin / manage_assets).
  { to: "/assets", label: "Asset Register", icon: BoxesIcon, requiredPermission: "manage_assets" },
  { to: "/entities", label: "Entities", icon: NetworkIcon, requiredPermission: "live_view" },
  { to: "/entities/bulk-import", label: "Bulk Import", icon: UploadIcon, requiredPermission: "manage_tenant" },
  { to: "/map", label: "Entity Map", icon: MapIcon, requiredPermission: "live_view" },
  // S18B-2 — command-center: e-Map (live health) + CMS operator dashboard.
  // Pages self-gate on the emap / cms_dashboard license features.
  { to: "/cms", label: "Command Center", icon: LayoutDashboardIcon, requiredPermission: "manage_cameras", requiredFeature: "cms_dashboard" },
  { to: "/emap", label: "Live Site Map", icon: MapIcon, requiredPermission: "live_view", requiredFeature: "emap" },
  { to: "/cameras", label: "Cameras", icon: VideoIcon, requiredPermission: "live_view" },
  { to: "/live", label: "Live view", icon: PlayCircleIcon, requiredPermission: "live_view" },
  { to: "/topology", label: "NVR Topology", icon: ServerIcon, requiredPermission: "live_view" },
  // S7 — saved camera grid layouts. Authenticated users only; no
  // permission gate so every operator + viewer can manage their own.
  { to: "/layouts", label: "Layouts", icon: LayoutGridIcon, requiredPermission: "live_view" },
  // S9 — operational health dashboard. Distinct from the
  // site/NVR-focused Equipment Health page already at /health (now
  // /equipment-health since this S9 route takes the /health slug).
  { to: "/health", label: "Health", icon: ActivityIcon, requiredPermission: "live_view" },
  { to: "/recordings", label: "Recordings", icon: CircleDotIcon, requiredPermission: "playback" },
  { to: "/equipment-health", label: "Equipment Health", icon: HeartPulseIcon, requiredPermission: "manage_cameras" },
  { to: "/alerts", label: "Alerts", icon: BellIcon, requiredPermission: "live_view" },
  // S15 — SInsight foundation: detections feed.
  { to: "/analytics/detections", label: "Analytics · Detections", icon: BrainCircuitIcon, requiredPermission: "manage_cameras" },
  // S16 — after-hours config: schedules + module enablement (admin).
  { to: "/analytics/config", label: "Analytics · Config", icon: BrainCircuitIcon, requiredPermission: "manage_cameras" },
  // S16-batch — zone/line shape drawing.
  { to: "/analytics/zones", label: "Analytics · Zones", icon: BrainCircuitIcon, requiredPermission: "manage_cameras" },
  // S16-fire — assistive fire/smoke config (beta).
  { to: "/analytics/fire", label: "Analytics · Fire", icon: BrainCircuitIcon, requiredPermission: "manage_cameras" },
  // S12 — evidence center + report builder.
  { to: "/evidence", label: "Evidence", icon: FilmIcon, requiredPermission: "export" },
  { to: "/reports", label: "Reports", icon: FileBarChart2Icon, requiredPermission: "view_global_reports" },
  { to: "/admin/cameras", label: "Admin · Cameras", icon: SlidersHorizontalIcon, requiredPermission: "manage_cameras" },
  // S17A — SOP/runbook engine + escalation matrix.
  { to: "/admin/runbooks", label: "Admin · Runbooks", icon: ScrollTextIcon, requiredPermission: "manage_alerts" },
  { to: "/admin/escalation", label: "Admin · Escalation", icon: ShieldCheckIcon, requiredPermission: "manage_alerts" },
  // S5 — device registry nav.
  { to: "/devices/profiles", label: "Device Profiles", icon: SlidersHorizontalIcon, requiredPermission: "manage_cameras" },
  { to: "/cameras/import", label: "Bulk Import Cameras", icon: UploadIcon, requiredPermission: "manage_cameras" },
  // S6 — ONVIF discovery.
  { to: "/devices/discovery", label: "ONVIF Discovery", icon: NetworkIcon, requiredPermission: "manage_cameras" },
  // U1c-3 — SLink edge-agent provisioning. Permission-derived gating (mirrors
  // the U1b Users entry): visible to anyone holding manage_devices (super-admin,
  // legacy admin, or a Tenant-Admin-by-role-grant), hidden otherwise.
  { to: "/devices/slink", label: "SLink Agents", icon: MonitorIcon, requiredPermission: "manage_devices" },
  // U1b-FE — permission-derived gating: Users + Roles show for anyone holding
  // manage_users (super-admin, legacy admin, or a Tenant-Admin-by-role-grant),
  // hidden for operator/viewer/auditor. Was adminOnly (legacy-role-only).
  { to: "/users", label: "Users", icon: UsersIcon, requiredPermission: "manage_users" },
  { to: "/roles", label: "Roles", icon: ShieldCheckIcon, requiredPermission: "manage_users" },
  // S4 — Settings group. The leaf pages handle their own permission
  // checks server-side; these are visible to every authenticated user
  // and SSO is hidden behind manage_tenant (the page itself 403s
  // otherwise).
  { to: "/settings/mfa", label: "Settings · MFA", icon: LockIcon },
  { to: "/settings/sessions", label: "Settings · Sessions", icon: MonitorIcon },
  { to: "/settings/api-tokens", label: "Settings · API Tokens", icon: KeySquareIcon },
  { to: "/settings/change-password", label: "Settings · Password", icon: SettingsIcon },
  { to: "/settings/sso", label: "Settings · SSO", icon: ScrollTextIcon, requiredPermission: "manage_tenant" },
  // S13a — notifications channels + rules CRUD (admin only).
  { to: "/settings/notifications", label: "Settings · Notifications", icon: BellIcon, requiredPermission: "manage_alerts" },
  // S13b — live-view transport (HLS / WebRTC).
  { to: "/settings/live-view", label: "Settings · Live View", icon: PlayCircleIcon, requiredPermission: "live_view" },
  // S13c — edge auth migration dashboard (admin only).
  { to: "/settings/edge-auth", label: "Settings · Edge Auth", icon: ShieldCheckIcon, requiredPermission: "manage_tenant" },
  // S14 — storage + retention policy (admin only).
  { to: "/settings/storage", label: "Settings · Storage", icon: DatabaseIcon, requiredPermission: "manage_tenant" },
  {
    to: "/audit",
    label: "Audit Log",
    icon: ShieldCheckIcon,
    requiredPermission: "view_audit_log",
  },
  { to: "/license", label: "License", icon: KeyRoundIcon, requiredPermission: "manage_tenant" },
  // S17B — plan/usage + white-label.
  { to: "/licensing", label: "Licensing & Usage", icon: BadgeCheckIcon, requiredPermission: "manage_tenant" },
  // S2 — super admin only; route guard in App.tsx blocks non-supers
  // even if the link were exposed. We hide it via Layout below.
  { to: "/admin/tenants", label: "Admin · Tenants", icon: ShieldCheckIcon, superAdminOnly: true },
];

export function Layout() {
  const { user } = useAuthStore();
  const isSuperAdmin = useTenantStore((s) => s.isSuperAdmin);
  const nav = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logoutApi();
    // The auth store alone is not the session: the persisted tenant store and
    // the response cache (signed URLs) go too. ONE path (store/session.ts).
    endSession();
    nav("/login", { replace: true });
  };

  // Subscribe to the realtime feed once at the layout level so every
  // page benefits from cache invalidation on alerts/status changes.
  useStatusFeed();
  const { data: alertSummary } = useAlertEventsSummary();
  // The `?.` guarded `alertSummary` and then dereferenced `.open` anyway, in
  // the component that wraps EVERY authenticated route. A 200 whose body lacks
  // `open` therefore threw here — and with no boundary above it that unmounted
  // the entire tree, sidebar included. One bad deref, whole product white.
  const criticalCount = alertSummary?.open?.critical ?? 0;

  // License feature map for nav feature-gating — the SAME source the e-Map /
  // Command Center pages self-gate on (useLicense().features). Default {} until
  // loaded, so a feature-gated item stays hidden until its flag is confirmed.
  const { data: license } = useLicense();
  const features = license?.features ?? {};

  const role = user?.role;

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className="hidden w-56 flex-col border-r bg-card md:flex">
        <Link
          to="/dashboard"
          className="flex h-14 items-center gap-2 border-b px-4 font-semibold"
        >
          <span className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground">
            SView360
          </span>
          <span className="text-xs text-muted-foreground">Cloud VMS</span>
          {isSuperAdmin && (
            <span className="ml-auto rounded bg-primary px-2 py-0.5 text-[10px] uppercase text-white">
              super
            </span>
          )}
        </Link>
        <nav className="flex flex-1 flex-col gap-1 p-2">
          {NAV.map((item) => {
            if (!navItemVisible(item, { role, isSuperAdmin, user })) return null;
            // Feature gate (Layout-level): hide items whose license feature the
            // tenant lacks, so a nav link never dead-ends on a "not available
            // on your plan" card. Shown only when the flag is confirmed true.
            if (item.requiredFeature && features[item.requiredFeature] !== true) {
              return null;
            }
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    navBase,
                    "hover:bg-muted",
                    isActive
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground",
                  )
                }
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1">{item.label}</span>
                {item.to === "/alerts" && criticalCount > 0 && (
                  <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white">
                    {criticalCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t p-3 text-xs">
          {user && (
            <div className="mb-2 truncate">
              <div className="font-medium text-foreground">{user.email}</div>
              <div className="text-muted-foreground">{user.role}</div>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleLogout}
          >
            <LogOutIcon className="mr-1 h-4 w-4" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex min-h-screen flex-1 flex-col">
        <TrialBanner />

        {/* Top utility bar — visible on desktop too; hosts the
            TenantSwitcher for super admins. */}
        <div className="hidden h-10 items-center justify-end gap-3 border-b bg-primary px-4 text-white md:flex">
          <NotificationBell />
          <TenantSwitcher />
        </div>

        {/* Mobile top bar (sidebar collapses on small screens) */}
        <header className="flex h-14 items-center justify-between border-b bg-card px-4 md:hidden">
          <Link to="/dashboard" className="font-semibold">
            <span className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground">
              SView360
            </span>
          </Link>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            Logout
          </Button>
        </header>

        <main className="flex-1 overflow-auto px-6 py-6">
          {/* Boundary INSIDE the chrome: a page crash leaves the sidebar,
              header and nav usable so the operator can navigate away instead
              of staring at a blank document. `resetKey` is the pathname, so
              leaving a crashed route clears the error — without it the first
              crash pins the state and every later nav click looks dead. */}
          <ErrorBoundary
            scope="route"
            resetKey={location.pathname}
            hint="The rest of the application is still working — use the navigation to move to another screen."
          >
            <Outlet />
          </ErrorBoundary>
        </main>

        <footer className="border-t bg-card">
          <div className="px-6 py-3 text-xs text-muted-foreground">
            <ActivityIcon className="mr-1 inline h-3 w-3" />
            SView360 · STQC-certified Cloud VMS · Seple Novaedge
          </div>
        </footer>
      </div>
    </div>
  );
}
